"use client";

import messages from "@/i18n/messages/en.json";

import { useAnalytics } from "@/lib/analytics/useAnalytics";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { useEffect, useRef, useState } from "react";
import { normalizePhoneInputValue } from "@artha/ui";

import { isValidWhatsAppNumber, normalizeCountryCode } from "@artha/utils";
import type { CandidateContactStatusDto } from "@artha/api-contracts";
import type {
  CandidateContactVerificationStepProps,
  CandidateContactVerificationAction,
  CandidateContactDelivery,
} from "../personalization.types";
import {
  startCandidateContactVerification,
  requestCandidateContactEmailLink,
  requestCandidatePhoneCode,
  verifyCandidatePhoneCode,
} from "../services/contactVerificationClient";
import { isCandidateIdentityConflict } from "../services/personalizationClient";

import {
  PHONE_CODE_PATTERN,
  CONTACT_RESEND_SECONDS,
  CONTACT_TIMER_INTERVAL_MS,
} from "../components/verification/verification.constants";

export function useCandidateContactVerification({
  username,
  creatorId,
  source,
  externalId,
  posthogDistinctId,
  profile,
  openToOpportunities,
  deliveryState = {},
  onDeliverySent,
  onPrepared,
  onComplete,
  onIdentityConflict,
}: CandidateContactVerificationStepProps) {
  const { trackEvent } = useAnalytics();
  const country = normalizeCountryCode(profile.location?.country || "");
  const phone = normalizePhoneInputValue(profile.phone, country);
  const [status, setStatus] = useState<CandidateContactStatusDto | null>(null);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [phoneWait, setPhoneWait] = useState(0);
  const [emailWait, setEmailWait] = useState(0);
  const [emailSending, setEmailSending] = useState(false);
  const [action, setAction] =
    useState<CandidateContactVerificationAction>("preparing");
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const mounted = useRef(false);

  const sendPhone = async (candidateId: string, number: string) => {
    if (!isValidWhatsAppNumber(number)) {
      setError(
        messages.talentNetworkUi
          .enter_a_valid_phone_number_including_the_country_code,
      );
      return;
    }
    setAction("sending");
    setError(null);
    try {
      await requestCandidatePhoneCode(username, {
        externalId: candidateId,
        source,
        phone: number,
      });
      if (!mounted.current) return;
      setSent(true);
      setCode("");
      setPhoneWait(CONTACT_RESEND_SECONDS);
      onDeliverySent?.("phone", {
        externalId: candidateId,
        email: profile.email.trim().toLowerCase(),
        phone: number,
        resendAt:
          Date.now() + CONTACT_RESEND_SECONDS * CONTACT_TIMER_INTERVAL_MS,
      });
    } catch (failure) {
      if (mounted.current) {
        if (isCandidateIdentityConflict(failure) && onIdentityConflict) {
          onIdentityConflict();
          return;
        }
        setError(
          failure instanceof Error
            ? failure.message
            : messages.talentNetworkUi.unable_to_send_the_phone_code,
        );
      }
    } finally {
      if (mounted.current) setAction(null);
    }
  };

  const sendEmail = async (candidateId: string) => {
    setEmailSending(true);
    try {
      const result = await requestCandidateContactEmailLink(username, {
        externalId: candidateId,
        source,
      });
      if (!mounted.current) return;
      setStatus(
        (current) =>
          current && {
            ...current,
            emailLinkError: undefined,
            ...result,
          },
      );
      if (result.emailLinkSent) {
        setEmailWait(CONTACT_RESEND_SECONDS);
        onDeliverySent?.("email", {
          externalId: candidateId,
          email: profile.email.trim().toLowerCase(),
          resendAt:
            Date.now() + CONTACT_RESEND_SECONDS * CONTACT_TIMER_INTERVAL_MS,
        });
      }
    } catch (failure) {
      if (mounted.current) {
        if (isCandidateIdentityConflict(failure) && onIdentityConflict) {
          onIdentityConflict();
          return;
        }
        setStatus(
          (current) =>
            current && {
              ...current,
              emailLinkSent: false,
              emailLinkError:
                failure instanceof Error
                  ? failure.message
                  : messages.talentNetworkUi
                      .unable_to_send_the_email_link_you_can_still,
            },
        );
      }
    } finally {
      if (mounted.current) setEmailSending(false);
    }
  };

  const prepare = async () => {
    setAction("preparing");
    setError(null);
    try {
      if (!posthogDistinctId)
        throw new Error(
          messages.talentNetworkUi
            .your_session_is_still_loading_please_try_again,
        );
      const result = await startCandidateContactVerification(username, {
        externalId,
        posthogDistinctId,
        source,
        editableProfile: { ...profile, phone },
        openToOpportunities,
        sendEmailLink: false,
      });
      if (!mounted.current) return;
      const remainingWait = (delivery?: CandidateContactDelivery) =>
        delivery?.externalId === result.externalId &&
        delivery.email === profile.email.trim().toLowerCase()
          ? Math.max(
              0,
              Math.ceil(
                (delivery.resendAt - Date.now()) / CONTACT_TIMER_INTERVAL_MS,
              ),
            )
          : 0;
      const emailRemaining = remainingWait(deliveryState.email);
      const phoneRemaining =
        deliveryState.phone?.phone === phone
          ? remainingWait(deliveryState.phone)
          : 0;
      setStatus({
        ...result,
        emailLinkSent: result.emailLinkSent || emailRemaining > 0,
      });
      onPrepared?.(result);
      setAction(null);
      if (!result.emailVerified) {
        if (emailRemaining) setEmailWait(emailRemaining);
        else void sendEmail(result.externalId);
      }
      if (result.phoneVerified) {
        if (result.emailVerified) onComplete();
        return;
      }
      if (phoneRemaining) {
        setSent(true);
        setPhoneWait(phoneRemaining);
      } else if (isValidWhatsAppNumber(phone))
        void sendPhone(result.externalId, phone);
      else
        setError(
          messages.talentNetworkUi
            .add_a_valid_phone_number_including_the_country_code,
        );
    } catch (failure) {
      if (mounted.current) {
        if (isCandidateIdentityConflict(failure) && onIdentityConflict) {
          onIdentityConflict();
          return;
        }
        setError(
          failure instanceof Error
            ? failure.message
            : messages.talentNetworkUi.unable_to_prepare_verification,
        );
        setAction(null);
      }
    }
  };

  useEffect(() => {
    mounted.current = true;
    if (!started.current) {
      started.current = true;
      void prepare();
    }
    return () => {
      mounted.current = false;
    };
    // One request per opened sheet, including React Strict Mode's effect replay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!phoneWait && !emailWait) return;
    const timer = window.setTimeout(() => {
      setPhoneWait((value) => Math.max(0, value - 1));
      setEmailWait((value) => Math.max(0, value - 1));
    }, CONTACT_TIMER_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [phoneWait, emailWait]);

  const verify = async () => {
    if (!status || !PHONE_CODE_PATTERN.test(code)) return;
    setAction("verifying");
    setError(null);
    try {
      const result = await verifyCandidatePhoneCode(username, {
        externalId: status.externalId,
        source,
        phone,
        code,
      });
      if (mounted.current) {
        trackEvent(ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, {
          portal_id: creatorId,
          username,
          candidate_external_id: result.externalId,
          mode: "profile",
          method: "phone_otp",
          source,
          has_resume: result.hasResume,
        });
        onComplete();
      }
    } catch (failure) {
      if (mounted.current) {
        if (isCandidateIdentityConflict(failure) && onIdentityConflict) {
          onIdentityConflict();
          return;
        }
        setError(
          failure instanceof Error
            ? failure.message
            : messages.talentNetworkUi.invalid_or_expired_phone_code,
        );
      }
    } finally {
      if (mounted.current) setAction(null);
    }
  };

  return {
    error,
    status,
    action,
    prepare,
    emailSending,
    emailWait,
    sendEmail,
    phone,
    sent,
    phoneWait,
    sendPhone,
    verify,
    code,
    setCode,
  };
}
